import { CommonModule, isPlatformBrowser } from '@angular/common';
import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { AudioPlayer } from '@freelancer/audio-player';
import { TrackingModule } from '@freelancer/tracking';
import { UiModule } from '@freelancer/ui';
import type { PictureBackgroundImageStyles } from '@freelancer/ui/picture';
import { PictureBgImageObjectFit } from '@freelancer/ui/picture';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';
import type {
  FFTSIZE,
  HexColor,
  Point,
} from './ai-consultant-visualizer.types';

@UntilDestroy({ className: 'AiConsultantVisualizerComponent' })
@Component({
  standalone: true,
  selector: 'app-ai-consultant-visualizer',
  imports: [CommonModule, TrackingModule, UiModule],
  template: `
    <div class="Visualizer">
      <canvas
        #canvas
        title="Visualizer Canvas"
        i18n-title="Visualizer Canvas"
        class="RenderCanvas"
      ></canvas>
      <fl-picture
        class="BackgroundImage"
        [src]="'ai-consultant/background-ai-consultant.png'"
        [isBackgroundImage]="true"
        [backgroundStyles]="backgroundStyles"
      >
      </fl-picture>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./ai-consultant-visualizer.component.scss'],
})
export class AiConsultantVisualizerComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() isPlaying = false;
  @Input() src: string;

  @Output() onAudioEnded = new EventEmitter<void>();

  private fftSize: FFTSIZE = 128;
  private audioContext: AudioContext;
  private audioSource: MediaElementAudioSourceNode;
  private analyserNode: AnalyserNode;
  private bufferLength = 64;
  private dataArray: Uint8Array | null = null;
  private lineMultiplayer = 50;

  @ViewChild('canvas', { read: ElementRef })
  canvas: ElementRef<HTMLCanvasElement>;
  ctx: CanvasRenderingContext2D | null;

  coreAngle = 0;
  coreSize = 30;
  colors: readonly HexColor[] = [
    { r: 144, g: 62, b: 254 },
    { r: 46, g: 238, b: 188 },
    { r: 144, g: 62, b: 254 },
  ];

  interpolatedColorsForCircle: readonly HexColor[] = [];

  barColors: readonly HexColor[] = [
    { r: 115, g: 254, b: 254 },
    { r: 252, g: 112, b: 251 },
    { r: 72, g: 254, b: 253 },
    { r: 252, g: 113, b: 254 },
  ];

  colorToUse: readonly HexColor[] = [];

  readonly backgroundStyles: PictureBackgroundImageStyles = {
    position: 'center top',
    positionDesktop: 'center',
    objectFit: PictureBgImageObjectFit.COVER,
  };

  audio: HTMLAudioElement | undefined;
  isIdle = true;

  animationRequestId: number;

  private initialized$ = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private audioPlayer: AudioPlayer,
  ) {}

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.ctx = this.canvas.nativeElement.getContext('2d');

      this.audioContext = new AudioContext();
      this.analyserNode = this.audioContext.createAnalyser();

      this.analyserNode.fftSize = this.fftSize;
      this.bufferLength = this.analyserNode.frequencyBinCount;
      this.dataArray = new Uint8Array(this.bufferLength);

      this.initalizeCanvas();

      this.interpolatedColorsForCircle = this.generateColorGradient(
        this.colors,
        this.bufferLength,
      );

      this.colorToUse = this.generateColorGradient(
        this.barColors,
        this.bufferLength,
      );

      this.drawGradientCircleInTheMiddle(this.interpolatedColorsForCircle);
      this.drawSunRays();
      this.initialized$.next(true);
    }
  }

  ngOnDestroy(): void {
    if (this.animationRequestId) {
      cancelAnimationFrame(this.animationRequestId);
    }
    this.audioPlayer.pause();
    this.isIdle = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('isPlaying' in changes) {
      if (!this.isPlaying) {
        this.audioPlayer.pause();
        this.isIdle = true;
      } else if (this.audio) {
        this.playAndVisualizeAudio();
        this.isIdle = false;
      }
    }

    if ('src' in changes && this.src) {
      this.isIdle = false;
      this.audio = undefined;
      firstValueFrom(
        this.initialized$.asObservable().pipe(
          filter(val => val),
          untilDestroyed(this),
        ),
      ).then(() => {
        this.audio = this.audioPlayer.initialize(this.src);
        if (this.audio) {
          this.setAudioElement(this.audio);
          this.playAndVisualizeAudio();
        }
      });
    }
  }

  initalizeCanvas(): void {
    this.clearCanvas();
    if (this.ctx) {
      this.ctx.canvas.width = 287;
      this.ctx.canvas.height = 370;
    }
  }

  updateLoop(): void {
    if (this.dataArray) {
      this.analyserNode.getByteFrequencyData(this.dataArray);
    }
    this.clearCanvas();
    this.drawGradientCircleInTheMiddle(this.interpolatedColorsForCircle);
    this.drawSunRays();

    if (!this.isIdle || !this.dataArray?.every(i => i === 0)) {
      this.animationRequestId = requestAnimationFrame(
        this.updateLoop.bind(this),
      );
    }
  }

  playAndVisualizeAudio(): void {
    if (this.audio === undefined) {
      return;
    }

    this.audioPlayer.play();
    this.animationRequestId = requestAnimationFrame(this.updateLoop.bind(this));

    this.audioPlayer.onFinish().then(() => {
      this.onAudioEnded.emit();
      this.isIdle = true;
    });
  }

  setAudioElement(audioElement: HTMLAudioElement): void {
    // If there's an old source, disconnect it
    if (this.audioSource) {
      this.audioSource.disconnect();
    }

    // Create a new source for the new audio element
    this.audioSource = this.audioContext.createMediaElementSource(audioElement);

    // Connect the new source to the analyser and the destination
    this.audioSource.connect(this.analyserNode);
    this.audioSource.connect(this.audioContext.destination);
    this.audioContext.resume();
  }

  generateColorGradient(
    colors: readonly HexColor[],
    targetLength: number,
  ): readonly HexColor[] {
    // If no colors are provided, return an empty array
    if (colors.length === 0) {
      return [];
    }
    // Initialize the output array
    const output: HexColor[] = [];

    // Calculate the size of each color subdivision
    const subdivisionSize = Math.floor(targetLength / (colors.length - 1));

    // Interpolate between the start color and the end color
    for (let i = 0; i < colors.length - 1; i++) {
      const startColor = colors[i];
      const endColor = colors[i + 1];

      for (let j = 0; j < subdivisionSize; j++) {
        const t = this.easeInOutSine(j / subdivisionSize);
        const interpolatedColor = {
          r: Math.round(startColor.r + (endColor.r - startColor.r) * t),
          g: Math.round(startColor.g + (endColor.g - startColor.g) * t),
          b: Math.round(startColor.b + (endColor.b - startColor.b) * t),
        };
        output.push(interpolatedColor);
      }
    }

    // Add the last color
    output.push(colors[colors.length - 1]);

    // If the target length is not a multiple of the number of colors, fill the rest with the last color
    while (output.length < targetLength) {
      output.push(colors[colors.length - 1]);
    }

    return output;
  }

  easeInOutSine(t: number): number {
    return -(Math.cos(Math.PI * t) - 1) / 2;
  }

  clearCanvas(): void {
    if (this.ctx) {
      this.ctx.clearRect(
        0,
        0,
        this.canvas.nativeElement.width,
        this.canvas.nativeElement.height,
      );
    }
  }

  drawSunRays(): void {
    const dataOnSunRay = this.dataArray
      ? Array.of(...this.dataArray)
      : new Array(this.bufferLength).fill(1);

    const dataToDraw = this.generateWaves(dataOnSunRay, 8, dataOnSunRay.length);
    if (this.ctx) {
      const centerX = this.ctx.canvas.width / 2;
      const centerY = this.ctx.canvas.height / 2;
      for (let i = 0; i < dataToDraw.length; i++) {
        const rayAngle = (i / dataToDraw.length) * 2 * Math.PI;
        const rayLength = 1 + dataToDraw[i];

        const x1 = centerX + Math.cos(rayAngle) * this.coreSize;
        const y1 = centerY + Math.sin(rayAngle) * this.coreSize;
        const x2 = centerX + Math.cos(rayAngle) * (this.coreSize + rayLength);
        const y2 = centerY + Math.sin(rayAngle) * (this.coreSize + rayLength);

        this.drawWaves(
          x1,
          y1,
          x2,
          y2,
          this.colorToUse[Math.floor(dataToDraw[i]) % this.colorToUse.length],
          i,
          [...dataToDraw],
        );
      }
    }
  }

  calculateDirectionVector(pointA: Point, pointB: Point): Point {
    // Calculate the components of the vector (delta x and delta y)
    const deltaX = pointB.x - pointA.x;
    const deltaY = pointB.y - pointA.y;

    // Calculate the magnitude of the vector
    const magnitude = Math.sqrt(deltaX ** 2 + deltaY ** 2);

    // Calculate the unit vector (direction vector)
    const directionVector = {
      x: deltaX / magnitude,
      y: deltaY / magnitude,
    };

    return directionVector;
  }

  drawWaves(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color: HexColor,
    index: number = 0,
    dataArray: readonly number[],
  ): void {
    const vector = this.calculateDirectionVector(
      { x: x1, y: y1 },
      { x: x2, y: y2 },
    );

    if (this.ctx && this.dataArray) {
      this.ctx.shadowBlur = 0;
      this.ctx.shadowColor = 'transparent';
      const highestPoint = dataArray.reduce((a, b) => Math.max(a, b));
      const multiplayer = dataArray[index] / highestPoint;
      const shadowMultiplayer =
        multiplayer > 0.2
          ? this.lineMultiplayer * multiplayer
          : this.lineMultiplayer * 0.2;

      // Creating the shadow glow at the back of each line
      const gradient = this.ctx.createLinearGradient(
        x1 - vector.x * shadowMultiplayer,
        y1 - vector.y * shadowMultiplayer,
        x2 + vector.x * shadowMultiplayer * 2,
        y2 + vector.y * shadowMultiplayer * 2,
      );
      gradient.addColorStop(0, `rgba(${color.r}, ${color.g}, ${color.b}, .5)`); // Start color with strongest alpha
      gradient.addColorStop(1, `rgba(${color.r}, ${color.g}, ${color.b}, 0)`); // End color with transparent alpha

      // Draw the first line with a linear gradient
      this.ctx.strokeStyle = gradient; // Use the linear gradient as the stroke style
      this.ctx.lineWidth = 10; // Adjust the line width for the first line
      this.ctx.lineCap = 'round'; // Set the line cap style to round for a smoother effect
      this.ctx.shadowBlur = 10;
      this.ctx.shadowColor = `rgba(${color.r},${color.g}, ${color.b}, 0.5)`;
      this.ctx.beginPath();
      this.ctx.moveTo(x1, y1); // Starting point of the first line
      this.ctx.lineTo(
        x2 + vector.x * this.lineMultiplayer * 2,
        y2 + vector.y * this.lineMultiplayer * 2,
      ); // Ending point of the first line
      this.ctx.stroke();

      this.ctx.shadowBlur = 0;
      this.ctx.shadowColor = 'transparent';

      // Draw the second line with the provided parameters
      const gradientLine = this.ctx.createLinearGradient(x1, y1, x2, y2);
      gradientLine.addColorStop(
        0,
        `rgba(${color.r}, ${color.g}, ${color.b}, 1)`,
      ); // Start color for the gradient
      gradientLine.addColorStop(1, 'rgba(0, 0, 0, .1)'); // End color with transparent alpha

      this.ctx.strokeStyle = `rgba(${color.r}, ${color.g}, ${color.b}, .9)`; // Use the gradient line as the stroke style
      this.ctx.lineWidth = 5; // Adjust the line width for the second line
      this.ctx.lineCap = 'round'; // Set the line cap style to round for a smoother effect
      this.ctx.beginPath();
      this.ctx.moveTo(x1 + vector.x * 5, y1 + vector.y * 5); // Starting point of the second line
      this.ctx.lineTo(x2, y2); // Ending point of the second line
      this.ctx.stroke();
    }
  }

  generateWaves(
    dataArray: readonly number[],
    numPoints: number,
    targetLength: number,
  ): readonly number[] {
    const distanceBetweenPoints = Math.floor(targetLength / numPoints);

    const values = [];
    if (dataArray.length > 0) {
      for (let i = 0; i < numPoints; i += 1) {
        values.push((i * 2) % dataArray.length);
      }
    }
    const indices = values;

    const output = new Array(targetLength).fill(0);

    for (let i = 0; i < indices.length; i++) {
      output[i * distanceBetweenPoints] = dataArray[indices[i]] * 0.2;
    }

    for (let i = 0; i < numPoints; i++) {
      const start = i * distanceBetweenPoints;
      const end = ((i + 1) % numPoints) * distanceBetweenPoints;
      const mid = end - start;
      output[mid] = dataArray[mid] * 0.2;
      const startValue = output[start];
      const endValue = i === numPoints - 1 ? output[0] : output[end];

      for (let j = start; j !== end; j = (j + 1) % targetLength) {
        if (output[j] === 0) {
          const t =
            ((j - start + targetLength) % targetLength) / distanceBetweenPoints;
          output[j] = startValue * (1 - t) + endValue * t;
        }
      }
    }

    return output;
  }

  drawGradientCircleInTheMiddle(colors: readonly HexColor[]): void {
    const centerX = this.canvas.nativeElement.width / 2;
    const centerY = this.canvas.nativeElement.height / 2;

    if (this.ctx) {
      this.ctx.shadowBlur = 0;
      this.ctx.shadowColor = 'transparent';

      this.coreSize =
        Math.min(this.ctx.canvas.width, this.ctx.canvas.height) * 0.25;
      const radius = this.coreSize - 10 > 0 ? this.coreSize - 10 : 0;

      // Calculate the number of color segments based on the length of the colors array
      const colorSegments = colors.length;

      // Calculate the angle step for each color segment
      const angleStep = (Math.PI * 1.3) / colorSegments;

      this.ctx.lineWidth = 5;
      this.ctx.lineCap = 'round';

      for (let i = 0; i < colorSegments; i++) {
        const currentColor = colors[i % colorSegments];
        const startAngle = this.coreAngle + i * angleStep;
        const endAngle = startAngle + angleStep;
        this.ctx.beginPath();
        this.ctx.arc(centerX, centerY, radius, startAngle, endAngle);
        this.ctx.strokeStyle = `rgba(${currentColor.r},${currentColor.g},${currentColor.b},1)`; // Use colors cyclically

        this.ctx.stroke();
        this.ctx.closePath();
      }

      this.ctx.shadowBlur = 0;
      this.ctx.shadowColor = 'transparent';
    }
  }
}
